// Identity federation and provisioning contracts (ADR-011; PB-17-04..07). Provider configuration is
// validated server-side with the protocol schemas in @passage/identity; secrets are write-only.
import { z } from 'zod';

const reason = z.string().trim().min(1, 'v.required').max(500, 'v.tooLong');
const label = z.string().trim().min(1, 'v.required').max(100, 'v.tooLong');
const version = z.number().int().min(1);
const secret = z.string().min(8, 'v.secretShort').max(2000, 'v.tooLong');

export const IDENTITY_PROTOCOLS = ['oidc', 'saml'] as const;
export const IDENTITY_PRESETS = ['generic', 'entra'] as const;
export type IdentityProtocol = (typeof IDENTITY_PROTOCOLS)[number];
export type IdentityPreset = (typeof IDENTITY_PRESETS)[number];

export const CreateIdentityProviderRequest = z.object({
  protocol: z.enum(IDENTITY_PROTOCOLS),
  preset: z.enum(IDENTITY_PRESETS).default('generic'),
  displayName: label,
  config: z.record(z.string(), z.unknown()),
  /** OIDC client secret (write-only, sealed at rest). */
  clientSecret: secret.optional(),
  jitProvisioning: z.boolean().default(false),
  reason,
});
export type CreateIdentityProviderRequest = z.infer<typeof CreateIdentityProviderRequest>;

export const UpdateIdentityProviderRequest = z.object({
  expectedVersion: version,
  displayName: label.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  jitProvisioning: z.boolean().optional(),
  reason,
});
export const RotateProviderSecretRequest = z.object({ expectedVersion: version, clientSecret: secret, reason });
export const ProviderStatusRequest = z.object({ expectedVersion: version, reason });

export interface CertificateSummary {
  readonly kind: 'certificate' | 'public_key';
  readonly fingerprintSha256: string;
  readonly subject: string | null;
  readonly validTo: string | null;
}

export interface IdentityProviderSummary {
  readonly id: string;
  readonly protocol: IdentityProtocol;
  readonly preset: IdentityPreset;
  readonly displayName: string;
  readonly status: 'draft' | 'active' | 'disabled';
  readonly version: number;
  readonly jitProvisioning: boolean;
  readonly linkedUsers: number;
  /** Whether the current configuration version passed a test sign-in (PD-104). */
  readonly testedCurrentVersion: boolean;
  readonly lastTestedAt: string | null;
  readonly lastTestOutcome: 'succeeded' | 'failed' | null;
  readonly updatedAt: string;
}

export interface MappingRuleResponse {
  readonly id: string;
  readonly source: 'sso_claim' | 'scim_group';
  readonly providerId: string | null;
  readonly claim: string | null;
  readonly claimValue: string | null;
  readonly scimGroupId: string | null;
  readonly kind: 'experience' | 'capability' | 'specialist';
  readonly code: string;
  readonly siteId: string | null;
  readonly allowElevated: boolean;
  readonly status: 'active' | 'disabled';
  readonly reason: string;
  readonly version: number;
  readonly grantsConferred: number;
}

export interface IdentityProviderDetail extends IdentityProviderSummary {
  /** Validated configuration (never secrets). */
  readonly config: Record<string, unknown>;
  readonly secret: { readonly configured: boolean; readonly version: number; readonly updatedAt: string | null };
  /** Values to register at the identity provider. */
  readonly serviceProvider: { readonly entityId: string; readonly acsUrl: string; readonly redirectUri: string; readonly metadataUrl: string };
  readonly certificates: readonly CertificateSummary[];
  readonly mappingRules: readonly MappingRuleResponse[];
}

export const CreateMappingRuleRequest = z.object({
  source: z.enum(['sso_claim', 'scim_group']),
  providerId: z.uuid().nullable().default(null),
  claim: z.string().trim().regex(/^[A-Za-z0-9_:./#-]{1,200}$/, 'v.claimName').nullable().default(null),
  claimValue: z.string().trim().min(1, 'v.required').max(256, 'v.tooLong').nullable().default(null),
  scimGroupId: z.uuid().nullable().default(null),
  kind: z.enum(['experience', 'capability', 'specialist']),
  code: z.string().trim().min(2).max(64),
  siteId: z.uuid().nullable().default(null),
  /** PD-108: required to map administrator experiences, specialist roles or sensitive capabilities. */
  allowElevated: z.boolean().default(false),
  reason,
});
export type CreateMappingRuleRequest = z.infer<typeof CreateMappingRuleRequest>;
export const MappingRuleStatusRequest = z.object({ expectedVersion: version, reason });

// ------------------------------------------------------------------ external identities on a user
export interface IdentityLinkResponse {
  readonly id: string;
  readonly providerId: string;
  readonly providerName: string;
  readonly subject: string;
  readonly linkedVia: 'local_proof' | 'administrator' | 'provisioned_subject' | 'jit';
  readonly createdAt: string;
  readonly lastSignInAt: string | null;
}
export const CreateIdentityLinkRequest = z.object({ providerId: z.uuid(), subject: z.string().trim().min(1, 'v.required').max(512, 'v.tooLong'), reason });
export const RevokeIdentityLinkRequest = z.object({ reason });
export interface UserProvisioningFacts {
  readonly source: 'scim' | 'directory' | 'sso_jit' | null;
  readonly externalId: string | null;
  readonly sourceName: string | null;
  readonly lastSyncedAt: string | null;
}
export interface UserIdentitiesResponse {
  readonly links: readonly IdentityLinkResponse[];
  readonly provisioning: UserProvisioningFacts;
}

// ------------------------------------------------------------------ SSO sign-in
export interface SsoStartResponse {
  /** Where the browser goes next (the identity provider). */
  readonly redirectUrl: string;
}
export const SsoFinishRequest = z.object({ result: z.string().min(20).max(200) });
export const SsoLinkRequest = z.object({ result: z.string().min(20).max(200), password: z.string().min(1, 'v.required').max(1024) });
/** Returned by finish when an existing account must prove its password once before linking (PD-102). */
export interface SsoLinkRequiredResponse {
  readonly next: 'link';
  readonly email: string;
  readonly providerName: string;
}
export interface SsoTestResult {
  readonly providerId: string;
  readonly outcome: 'succeeded' | 'failed';
  readonly failureReason: string | null;
  readonly subject: string | null;
  readonly email: string | null;
  readonly emailVerified: boolean;
  readonly displayName: string | null;
  readonly groups: readonly string[];
  readonly idpMfa: boolean;
  /** What a real sign-in would do now (no session or account is created by a test). */
  readonly wouldResolve: 'linked_user' | 'link_with_password' | 'provisioned_subject' | 'jit_create' | 'no_account';
  readonly matchedUser: { readonly id: string; readonly email: string } | null;
  readonly grantsFromMappings: ReadonlyArray<{ readonly kind: string; readonly code: string; readonly siteId: string | null }>;
  readonly testedVersion: number;
}

// ------------------------------------------------------------------ SCIM administration
export interface ScimTokenSummary {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly createdAt: string;
  readonly expiresAt: string | null;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
}
export const CreateScimTokenRequest = z.object({ label, expiresInDays: z.number().int().min(1, 'v.range').max(730, 'v.range').nullable().default(365), reason });
export interface ScimTokenIssued {
  /** Shown once; only a hash is stored (PD-106). */
  readonly token: string;
  readonly summary: ScimTokenSummary;
}
export const RevokeScimTokenRequest = z.object({ reason });
export interface ScimGroupSummary {
  readonly id: string;
  readonly displayName: string;
  readonly externalId: string | null;
  readonly members: number;
  readonly deleted: boolean;
}

// ------------------------------------------------------------------ directory synchronisation
export const DIRECTORY_KINDS = ['simulator', 'ldap'] as const;
/** PD-592: the LDAP bind password — write-only, sealed at rest, never returned, logged or audited. */
const bindPassword = z.string().min(1, 'v.required').max(1024, 'v.tooLong');
export const CreateDirectoryRequest = z.object({
  kind: z.enum(DIRECTORY_KINDS),
  displayName: label,
  config: z.record(z.string(), z.unknown()),
  bindSecretRef: z.string().trim().regex(/^[A-Z][A-Z0-9_]{2,63}$/, 'v.secretRef').nullable().default(null),
  /** Sealed instead of a deployment secret reference; one or the other (PD-592). */
  bindPassword: bindPassword.optional(),
  scheduleMinutes: z.number().int().min(15, 'v.range').max(10080, 'v.range').nullable().default(null),
  maxDeactivationPercent: z.number().int().min(0, 'v.range').max(100, 'v.range').default(10),
  reason,
});
export type CreateDirectoryRequest = z.infer<typeof CreateDirectoryRequest>;
export const UpdateDirectoryRequest = z.object({
  expectedVersion: version,
  displayName: label.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  bindSecretRef: z.string().trim().regex(/^[A-Z][A-Z0-9_]{2,63}$/, 'v.secretRef').nullable().optional(),
  scheduleMinutes: z.number().int().min(15, 'v.range').max(10080, 'v.range').nullable().optional(),
  maxDeactivationPercent: z.number().int().min(0, 'v.range').max(100, 'v.range').optional(),
  status: z.enum(['active', 'disabled']).optional(),
  reason,
});
/** PD-592: store (or rotate) the sealed bind password; replaces any secret reference. */
export const RotateDirectorySecretRequest = z.object({ expectedVersion: version, bindPassword, reason });
export const RunDirectorySyncRequest = z.object({
  /** Apply deactivations even above the threshold, after review (PD-111). */
  confirmDeactivations: z.boolean().default(false),
  reason,
});
export interface DirectoryRunResponse {
  readonly id: string;
  readonly trigger: 'manual' | 'scheduled';
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** 'unknown': the directory timed out — nothing changed and the run is not a success (PD-594). */
  readonly outcome: 'succeeded' | 'failed' | 'held' | 'unknown' | null;
  readonly counts: { readonly created: number; readonly updated: number; readonly deactivated: number; readonly reactivated: number; readonly unchanged: number; readonly conflicts: number };
  readonly errorCode: string | null;
  readonly conflicts: ReadonlyArray<{ readonly externalId: string | null; readonly kind: string; readonly detail: Record<string, unknown> | null }>;
}
export interface DirectoryConnectionSummary {
  readonly id: string;
  readonly kind: 'simulator' | 'ldap';
  readonly displayName: string;
  readonly status: 'active' | 'disabled';
  readonly version: number;
  readonly config: Record<string, unknown>;
  readonly bindSecretRef: string | null;
  readonly scheduleMinutes: number | null;
  readonly maxDeactivationPercent: number;
  readonly lastRunAt: string | null;
  readonly lastSuccessAt: string | null;
  readonly lastOutcome: 'succeeded' | 'failed' | 'held' | 'unknown' | null;
  readonly lastError: string | null;
  /** Last-known state is older than two schedule intervals (or the last run failed): show a warning. */
  readonly stale: boolean;
  readonly ownedAccounts: number;
  /** PD-592: whether a bind credential is configured and where; never the value. */
  readonly bindSecret: { readonly source: 'sealed' | 'reference' | null; readonly version: number; readonly updatedAt: string | null };
  /** PD-597: last test connection. */
  readonly lastTest: { readonly at: string; readonly outcome: 'succeeded' | 'failed' | 'unknown'; readonly errorCode: string | null } | null;
}

/** PD-597: test connection = bind + one sample search. Counts only, never names or identifiers. */
export interface DirectoryTestResponse {
  readonly outcome: 'succeeded' | 'failed' | 'unknown';
  readonly errorCode: string | null;
  readonly durationMs: number;
  readonly counts: {
    readonly sampled: number;
    readonly withExternalId: number;
    readonly withEmail: number;
    readonly withDisplayName: number;
    readonly disabled: number;
    readonly mapped: Readonly<Record<'employeeId' | 'department' | 'title' | 'office', number | null>>;
  } | null;
}

export interface IdentityOverviewResponse {
  readonly entitlements: { readonly sso: boolean; readonly scim: boolean; readonly directory: boolean };
  readonly providers: readonly IdentityProviderSummary[];
  readonly scim: { readonly baseUrl: string; readonly tokens: readonly ScimTokenSummary[]; readonly groups: readonly ScimGroupSummary[]; readonly mappingRules: readonly MappingRuleResponse[]; readonly provisionedUsers: number };
  readonly directories: readonly DirectoryConnectionSummary[];
}

// ------------------------------------------------------------------ system console summary (ADR-011)
export interface PlatformIdentitySummary {
  readonly providers: ReadonlyArray<{ readonly id: string; readonly protocol: string; readonly preset: string; readonly displayName: string; readonly status: string; readonly version: number; readonly secretVersion: number; readonly linkedUsers: number; readonly lastTestOutcome: string | null; readonly updatedAt: string }>;
  readonly scimTokens: ReadonlyArray<{ readonly id: string; readonly label: string; readonly hint: string; readonly lastUsedAt: string | null; readonly expiresAt: string | null; readonly revokedAt: string | null }>;
  readonly directories: ReadonlyArray<{ readonly id: string; readonly kind: string; readonly displayName: string; readonly status: string; readonly lastOutcome: string | null; readonly lastSuccessAt: string | null; readonly lastError: string | null }>;
  readonly elevatedMappingRules: number;
  readonly provisionedUsers: { readonly scim: number; readonly directory: number; readonly ssoJit: number };
}

// ------------------------------------------------------------------ role preview (PB-17-01)
export interface AccessCatalogueResponse {
  readonly experiences: ReadonlyArray<{ readonly id: string; readonly tenantScopeAllowed: boolean; readonly actions: readonly string[] }>;
  readonly specialistRoles: ReadonlyArray<{ readonly id: string; readonly scope: 'site' | 'tenant' | 'either'; readonly actions: readonly string[] }>;
  readonly actions: ReadonlyArray<{ readonly id: string; readonly scope: 'tenant' | 'site'; readonly sensitive: boolean; readonly entitlement: string | null; readonly features: readonly string[] }>;
}
